import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { motion } from "framer-motion";
import { Gauge, Clock, Zap, AlertTriangle, TrendingUp, Server, Activity, ShieldCheck, DollarSign } from "lucide-react";

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(var(--success))",
  "hsl(var(--destructive))",
];

export default function Performance() {
  const { data: decisions = [], isLoading: loadingDecisions } = useQuery({
    queryKey: ["perf-decisions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("model_decisions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data;
    },
  });

  const { data: feedback = [], isLoading: loadingFeedback } = useQuery({
    queryKey: ["perf-feedback"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_feedback")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data;
    },
  });

  const { data: usage = [] } = useQuery({
    queryKey: ["perf-usage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("usage_tracking")
        .select("*")
        .eq("period_key", new Date().toISOString().slice(0, 7))
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  const { data: orgs = [] } = useQuery({
    queryKey: ["perf-orgs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, monthly_message_limit, monthly_ai_calls_limit");
      if (error) throw error;
      return data;
    },
  });

  const isLoading = loadingDecisions || loadingFeedback;

  // KPIs
  const totalCalls = decisions.length;
  const avgLatency = totalCalls > 0
    ? Math.round(decisions.reduce((s, d) => s + (d.latency_ms || 0), 0) / totalCalls)
    : 0;
  const p95Latency = totalCalls > 0
    ? (() => {
        const sorted = decisions.map(d => d.latency_ms || 0).sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length * 0.95)] || 0;
      })()
    : 0;
  const errorRate = totalCalls > 0
    ? ((decisions.filter(d => !d.success).length / totalCalls) * 100).toFixed(1)
    : "0";
  const fallbackRate = totalCalls > 0
    ? ((decisions.filter(d => d.was_fallback).length / totalCalls) * 100).toFixed(1)
    : "0";

  // SLA compliance: % of requests < 5s
  const slaCompliance = totalCalls > 0
    ? ((decisions.filter(d => (d.latency_ms || 0) < 5000).length / totalCalls) * 100).toFixed(1)
    : "100";

  // Circuit breaker status (derived from error rate in last 10 decisions per provider)
  const recentByProvider: Record<string, { total: number; errors: number }> = {};
  decisions.slice(0, 100).forEach(d => {
    const p = d.provider_used;
    if (!recentByProvider[p]) recentByProvider[p] = { total: 0, errors: 0 };
    recentByProvider[p].total += 1;
    if (!d.success) recentByProvider[p].errors += 1;
  });
  const circuitStatuses = Object.entries(recentByProvider).map(([name, s]) => {
    const errPct = s.total > 0 ? (s.errors / s.total) * 100 : 0;
    let status: "closed" | "open" | "half-open" = "closed";
    if (errPct > 50) status = "open";
    else if (errPct > 20) status = "half-open";
    return { name, status, errPct: errPct.toFixed(0) };
  });

  // Latency over time
  const latencyByHour: Record<string, { total: number; count: number }> = {};
  decisions.forEach(d => {
    if (!d.created_at || !d.latency_ms) return;
    const hour = d.created_at.slice(0, 13);
    if (!latencyByHour[hour]) latencyByHour[hour] = { total: 0, count: 0 };
    latencyByHour[hour].total += d.latency_ms;
    latencyByHour[hour].count += 1;
  });
  const latencyChart = Object.entries(latencyByHour)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-24)
    .map(([hour, v]) => ({
      hour: hour.slice(11) + "h",
      avg: Math.round(v.total / v.count),
      count: v.count,
    }));

  // Provider performance
  const providerStats: Record<string, { calls: number; errors: number; totalLatency: number; totalCost: number }> = {};
  decisions.forEach(d => {
    const p = d.provider_used;
    if (!providerStats[p]) providerStats[p] = { calls: 0, errors: 0, totalLatency: 0, totalCost: 0 };
    providerStats[p].calls += 1;
    if (!d.success) providerStats[p].errors += 1;
    providerStats[p].totalLatency += d.latency_ms || 0;
    providerStats[p].totalCost += Number(d.estimated_cost_usd || 0);
  });
  const providerTable = Object.entries(providerStats)
    .map(([name, s]) => ({
      name,
      calls: s.calls,
      avgLatency: Math.round(s.totalLatency / s.calls),
      errorRate: ((s.errors / s.calls) * 100).toFixed(1),
      cost: s.totalCost.toFixed(4),
    }))
    .sort((a, b) => b.calls - a.calls);

  // Cost per org
  const costByOrg: Record<string, { orgName: string; totalCost: number; calls: number }> = {};
  decisions.forEach(d => {
    const oid = d.org_id;
    if (!costByOrg[oid]) {
      const orgName = orgs.find(o => o.id === oid)?.name || oid.slice(0, 8);
      costByOrg[oid] = { orgName, totalCost: 0, calls: 0 };
    }
    costByOrg[oid].totalCost += Number(d.estimated_cost_usd || 0);
    costByOrg[oid].calls += 1;
  });
  const costOrgTable = Object.values(costByOrg)
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, 20);

  // Skill execution times
  const skillStats: Record<string, { count: number; totalTime: number }> = {};
  feedback.forEach(f => {
    if (!skillStats[f.skill_name]) skillStats[f.skill_name] = { count: 0, totalTime: 0 };
    skillStats[f.skill_name].count += 1;
    skillStats[f.skill_name].totalTime += f.execution_time_ms || 0;
  });
  const skillChart = Object.entries(skillStats)
    .map(([name, s]) => ({ name, calls: s.count, avgTime: Math.round(s.totalTime / s.count) }))
    .sort((a, b) => b.calls - a.calls)
    .slice(0, 8);

  // Rate limit status per org
  const rateLimits = orgs.map(org => {
    const orgUsage = usage.filter(u => u.org_id === org.id);
    const msgUsed = orgUsage.find(u => u.metric === "messages")?.value || 0;
    const aiUsed = orgUsage.find(u => u.metric === "ai_calls")?.value || 0;
    const msgLimit = org.monthly_message_limit || 1000;
    const aiLimit = org.monthly_ai_calls_limit || 500;
    return {
      name: org.name,
      msgUsed,
      msgLimit,
      msgPct: Math.min(100, Math.round((msgUsed / msgLimit) * 100)),
      aiUsed,
      aiLimit,
      aiPct: Math.min(100, Math.round((aiUsed / aiLimit) * 100)),
    };
  });

  // Error distribution pie
  const errorTypes: Record<string, number> = {};
  decisions.filter(d => !d.success).forEach(d => {
    const t = d.error_type || "unknown";
    errorTypes[t] = (errorTypes[t] || 0) + 1;
  });
  const errorPie = Object.entries(errorTypes).map(([name, value]) => ({ name, value }));

  const totalCostUsd = decisions.reduce((s, d) => s + Number(d.estimated_cost_usd || 0), 0);

  const kpis = [
    { label: "Latência Média", value: `${avgLatency}ms`, icon: Clock, color: "text-primary" },
    { label: "P95 Latência", value: `${p95Latency}ms`, icon: Gauge, color: "text-warning" },
    { label: "Taxa de Erro", value: `${errorRate}%`, icon: AlertTriangle, color: "text-destructive" },
    { label: "SLA (<5s)", value: `${slaCompliance}%`, icon: ShieldCheck, color: "text-success" },
    { label: "Custo Total", value: `$${totalCostUsd.toFixed(4)}`, icon: DollarSign, color: "text-info" },
    { label: "Total Chamadas", value: totalCalls.toLocaleString(), icon: TrendingUp, color: "text-primary" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Activity className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold font-headline text-foreground">Performance & Operações</h1>
          <p className="text-sm text-muted-foreground">Monitoramento de latência, circuit breaker, SLA e custos</p>
        </div>
      </div>

      {/* KPIs */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {kpis.map((k, i) => (
            <motion.div key={k.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{k.label}</span>
                    <k.icon className={`w-4 h-4 ${k.color}`} />
                  </div>
                  <div className="text-2xl font-bold font-headline text-foreground">{k.value}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Circuit Breaker Status */}
      {circuitStatuses.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="w-4 h-4" /> Circuit Breaker Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {circuitStatuses.map(cb => (
                <div key={cb.name} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/30">
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    cb.status === "closed" ? "bg-green-500" : cb.status === "half-open" ? "bg-yellow-500" : "bg-red-500"
                  }`} />
                  <span className="text-sm font-medium text-foreground">{cb.name}</span>
                  <Badge variant={cb.status === "closed" ? "secondary" : cb.status === "half-open" ? "outline" : "destructive"} className="text-xs">
                    {cb.status === "closed" ? "OK" : cb.status === "half-open" ? "Degraded" : "Open"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{cb.errPct}% erros</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="latency" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="latency">Latência</TabsTrigger>
          <TabsTrigger value="providers">Provedores</TabsTrigger>
          <TabsTrigger value="costs">Custos</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="limits">Rate Limits</TabsTrigger>
          <TabsTrigger value="errors">Erros</TabsTrigger>
        </TabsList>

        {/* Latency Tab */}
        <TabsContent value="latency" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Latência Média por Hora (últimas 24h)</CardTitle>
            </CardHeader>
            <CardContent>
              {latencyChart.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem dados de latência ainda.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={latencyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis dataKey="hour" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} unit="ms" />
                    <Tooltip
                      contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }}
                      formatter={(v: number) => [`${v}ms`, "Latência"]}
                    />
                    <Line type="monotone" dataKey="avg" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Throughput por Hora</CardTitle>
            </CardHeader>
            <CardContent>
              {latencyChart.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem dados.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={latencyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis dataKey="hour" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }} />
                    <Bar dataKey="count" fill="hsl(var(--info))" radius={[4, 4, 0, 0]} name="Chamadas" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Performance por Provedor</CardTitle>
            </CardHeader>
            <CardContent>
              {providerTable.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem dados de provedores.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provedor</TableHead>
                      <TableHead className="text-right">Chamadas</TableHead>
                      <TableHead className="text-right">Latência Média</TableHead>
                      <TableHead className="text-right">Taxa Erro</TableHead>
                      <TableHead className="text-right">Custo (USD)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providerTable.map(p => (
                      <TableRow key={p.name}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="text-right">{p.calls}</TableCell>
                        <TableCell className="text-right">{p.avgLatency}ms</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={Number(p.errorRate) > 5 ? "destructive" : "secondary"}>{p.errorRate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right">${p.cost}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Custo por Organização</CardTitle>
            </CardHeader>
            <CardContent>
              {costOrgTable.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem dados de custo.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organização</TableHead>
                      <TableHead className="text-right">Chamadas</TableHead>
                      <TableHead className="text-right">Custo Total (USD)</TableHead>
                      <TableHead className="text-right">Custo Médio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costOrgTable.map(c => (
                      <TableRow key={c.orgName}>
                        <TableCell className="font-medium">{c.orgName}</TableCell>
                        <TableCell className="text-right">{c.calls}</TableCell>
                        <TableCell className="text-right">${c.totalCost.toFixed(4)}</TableCell>
                        <TableCell className="text-right">${(c.totalCost / c.calls).toFixed(6)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skills Tab */}
        <TabsContent value="skills">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Tempo de Execução por Skill</CardTitle>
            </CardHeader>
            <CardContent>
              {skillChart.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem dados de skills.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={skillChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} unit="ms" />
                    <YAxis dataKey="name" type="category" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={120} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }}
                      formatter={(v: number) => [`${v}ms`, "Tempo Médio"]}
                    />
                    <Bar dataKey="avgTime" fill="hsl(var(--warning))" radius={[0, 4, 4, 0]} name="Tempo Médio" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rate Limits Tab */}
        <TabsContent value="limits" className="space-y-4">
          {rateLimits.length === 0 ? (
            <Card><CardContent className="py-8"><p className="text-sm text-muted-foreground text-center">Nenhuma organização encontrada.</p></CardContent></Card>
          ) : (
            rateLimits.map(rl => (
              <Card key={rl.name}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{rl.name}</CardTitle>
                    <Server className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Mensagens: {rl.msgUsed.toLocaleString()} / {rl.msgLimit.toLocaleString()}</span>
                      <span className={rl.msgPct > 90 ? "text-destructive font-medium" : ""}>{rl.msgPct}%</span>
                    </div>
                    <Progress value={rl.msgPct} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Chamadas IA: {rl.aiUsed.toLocaleString()} / {rl.aiLimit.toLocaleString()}</span>
                      <span className={rl.aiPct > 90 ? "text-destructive font-medium" : ""}>{rl.aiPct}%</span>
                    </div>
                    <Progress value={rl.aiPct} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Distribuição de Erros</CardTitle>
            </CardHeader>
            <CardContent>
              {errorPie.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum erro registrado 🎉</p>
              ) : (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={errorPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                        {errorPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {errorPie.map((e, i) => (
                      <div key={e.name} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-muted-foreground">{e.name}:</span>
                        <span className="font-medium">{e.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
